﻿import React from 'react';

import { Loader } from '@progress/kendo-react-indicators';
import { useAppSelector } from '../../../app/hooks/store-hooks';
import { useFlightWorker } from '../../../app/hooks/use-flight-worker';
import { diversionSettingsSelector } from '../../../app/state/selectors';
import { DiversionAirportProperties } from '../../../layers/diversion-status-layer';
import { DiversionManagementInfo, MessageType } from '../../../services/actor-messages';
import { DiversionTooltipData } from '../../../services/map-interaction-service';
import {
    formatCapacityValueByType,
    formatPriority,
    formatStatus,
    formatTime,
    formatTimeOnboardAircraft,
    formatTotalCapacity,
    getCapacityHeaderText
} from '../../diversion/diversion-formatters';

export function DiversionTooltipFromItem({ item }: { item: DiversionAirportProperties }) {
    const [data, setData] = React.useState<DiversionManagementInfo | null>(null);
    const [loading, setLoading] = React.useState(true);
    const settings = useAppSelector(diversionSettingsSelector);
    const worker = useFlightWorker();
    const priorityType = useAppSelector((x) => x.settings.diversionPriorityType);

    React.useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                const info = await worker.actor.sendAsync({
                    type: MessageType.getDiversionInfo,
                    data:
                        item.icaoId && item.icaoId.trim()
                            ? item.icaoId
                            : item.iataId && item.iataId.trim()
                              ? item.iataId
                              : item.faaId && item.faaId.trim()
                                ? item.faaId
                                : ''
                });
                if (mounted) {
                    setData(info);
                }
            } catch (e) {
                console.error('Failed to fetch diversion info', e);
            } finally {
                if (mounted) {
                    setLoading(false);
                }
            }
        })();
        return () => {
            mounted = false;
        };
    }, [worker, item.icaoId, item.iataId, item.faaId]);

    if (loading) {
        return (
            <div style={{ padding: 6, minHeight: 150, minWidth: 350 }}>
                <Loader size="small" />
            </div>
        );
    }
    if (!data || !data.diversionInfo) {
        return <div style={{ padding: 6, fontSize: 11 }}>No diversion info</div>;
    }

    const di = data.diversionInfo;
    const totalDiverts = (di.divert.airline.total ?? 0) + (di.divert.network.total ?? 0);
    const flights = di.planes?.items ? di.planes.items.slice(0, 8) : [];

    return (
        <div
            style={{
                fontSize: 11,
                lineHeight: 1.25,
                maxWidth: 420,
                color: 'var(--text-color)'
            }}>
            {/* Header */}
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontWeight: 600,
                    marginBottom: 4
                }}>
                <span title={data.airportName}>
                    {[item.faaId, item.iataId, item.icaoId].filter((x) => x && x.trim()).join(' / ') || data.airportName}
                </span>
                <span>
                    Div {totalDiverts}/{di.totalDiversions} | Cap {formatTotalCapacity(di.capacity, settings)}
                </span>
            </div>

            {/* Capacity Mini Table */}
            <table
                style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    marginBottom: 6
                }}>
                <thead>
                    <tr>
                        <th style={thMini}></th>
                        <th style={thMini}>{getCapacityHeaderText('Airline', settings)}</th>
                        <th style={thMini}>{getCapacityHeaderText('Network', settings)}</th>
                        <th style={thMini}>{getCapacityHeaderText('Other', settings)}</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td style={tdMiniLabel}>Cap</td>
                        <td style={tdMini}>
                            {formatCapacityValueByType(
                                di.capacity.airline.narrow,
                                di.capacity.airline.wide,
                                di.capacity.airline.total,
                                settings.capacity.airline
                            )}
                        </td>
                        <td style={tdMini}>
                            {formatCapacityValueByType(
                                di.capacity.network.narrow,
                                di.capacity.network.wide,
                                di.capacity.network.total,
                                settings.capacity.network
                            )}
                        </td>
                        <td style={tdMini}>
                            {formatCapacityValueByType(
                                di.capacity.other.narrow,
                                di.capacity.other.wide,
                                di.capacity.other.total,
                                settings.capacity.other
                            )}
                        </td>
                    </tr>
                    <tr>
                        <td style={tdMiniLabel}>Act</td>
                        <td style={tdMini}>
                            {formatCapacityValueByType(
                                di.divert.airline.narrow,
                                di.divert.airline.wide,
                                di.divert.airline.total,
                                settings.capacity.airline,
                                '0'
                            )}
                        </td>
                        <td style={tdMini}>
                            {formatCapacityValueByType(
                                di.divert.network.narrow,
                                di.divert.network.wide,
                                di.divert.network.total,
                                settings.capacity.network,
                                '0'
                            )}
                        </td>
                        <td style={tdMini}>
                            {formatCapacityValueByType(
                                di.divert.other.narrow,
                                di.divert.other.wide,
                                di.divert.other.total,
                                settings.capacity.other,
                                '0'
                            )}
                        </td>
                    </tr>
                    <tr>
                        <td style={tdMiniLabel}>Avail</td>
                        <td style={tdMini}>
                            {formatCapacityValueByType(
                                di.available.airline.narrow,
                                di.available.airline.wide,
                                di.available.airline.total,
                                settings.capacity.airline
                            )}
                        </td>
                        <td style={tdMini}>
                            {formatCapacityValueByType(
                                di.available.network.narrow,
                                di.available.network.wide,
                                di.available.network.total,
                                settings.capacity.network
                            )}
                        </td>
                        <td style={tdMini}>
                            {formatCapacityValueByType(
                                di.available.other.narrow,
                                di.available.other.wide,
                                di.available.other.total,
                                settings.capacity.other
                            )}
                        </td>
                    </tr>
                </tbody>
            </table>

            {/* Flights Diverting */}
            <div style={{ fontWeight: 600, marginBottom: 2 }}>Flights Diverting</div>
            <div
                style={{
                    maxHeight: 150,
                    border: '1px solid var(--border-color)',
                    borderRadius: 3
                }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                        <tr>
                            <th style={thFlights}>AC</th>
                            <th style={thFlights}>Type</th>
                            <th style={thFlights}>Orig</th>
                            <th style={thFlights}>Dest</th>
                            <th style={thFlights}>Sts</th>
                            <th style={thFlights}>Time(+/-)</th>
                            <th style={thFlights}>Pri</th>
                            <th style={thFlights}>OnBd</th>
                        </tr>
                    </thead>
                    <tbody>
                        {flights.length === 0 && (
                            <tr>
                                <td style={emptyCell} colSpan={8}>
                                    No flights
                                </td>
                            </tr>
                        )}
                        {flights.map((p: any, i: number) => (
                            <tr key={p.recordId || i} style={i % 2 ? rowAlt : undefined}>
                                <td style={tdFlights}>{p.acid}</td>
                                <td style={tdFlights}>{p.aircraftType || ''}</td>
                                <td style={tdFlights}>{p.origin}</td>
                                <td style={tdFlights}>{p.originalDestination}</td>
                                <td style={tdFlights}>{formatStatus(p.status, p.subStatus)}</td>
                                <td style={tdFlights}>{formatTime(p.status === 0 ? p.flightTime : p.groundTime)}</td>
                                <td style={tdFlights}>{formatPriority(p.recoveryPriority, priorityType)}</td>
                                <td style={tdFlights}>{formatTimeOnboardAircraft(p.blockTime)}</td>
                            </tr>
                        ))}
                        {di.planes?.items?.length > flights.length && (
                            <tr>
                                <td style={{ ...tdFlights, fontStyle: 'italic' }} colSpan={8}>
                                    + {di.planes.items.length - flights.length} more...
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Optional Comment / Phone (tiny) */}
            {(di.phoneNumber || di.comments) && (
                <div style={{ marginTop: 4 }}>
                    {di.phoneNumber && <span style={{ marginRight: 8 }}>Ph: {di.phoneNumber}</span>}
                    {di.comments && (
                        <span
                            style={{
                                maxWidth: 360,
                                display: 'inline-block',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                            }}
                            title={di.comments}>
                            {di.comments}
                        </span>
                    )}
                </div>
            )}
        </div>
    );
}

const thMini: React.CSSProperties = {
    fontSize: 10,
    padding: '2px 4px',
    textAlign: 'center',
    fontWeight: 600,
    background: 'var(--alternate-color)'
};
const tdMini: React.CSSProperties = {
    fontSize: 11,
    padding: '2px 4px',
    textAlign: 'center'
};
const tdMiniLabel: React.CSSProperties = { ...tdMini, fontWeight: 600, textAlign: 'left' };

const thFlights: React.CSSProperties = {
    fontSize: 10,
    padding: '2px 4px',
    background: 'var(--alternate-color)',
    position: 'sticky',
    top: 0
};
const tdFlights: React.CSSProperties = {
    fontSize: 10.5,
    padding: '2px 4px',
    whiteSpace: 'nowrap'
};
const rowAlt: React.CSSProperties = { background: 'var(--row-alt-color, rgba(255,255,255,0.04))' };
const emptyCell: React.CSSProperties = { ...tdFlights, textAlign: 'center', fontStyle: 'italic' };

export function DiversionAirportTooltip(props: { info: DiversionTooltipData }) {
    const context = props.info;

    if (!context.items || context.items.length === 0) {
        return <></>;
    }

    return (
        <div
            style={{
                backgroundColor: 'var(--bg-color)',
                padding: 6,
                maxHeight: 400
            }}>
            {context.items.map((x, i) => (
                <div key={i} style={{ marginBottom: context.items.length > 1 ? 8 : 0 }}>
                    <DiversionTooltipFromItem item={x} />
                </div>
            ))}
        </div>
    );
}
